/**
 * Maps a server-supplied character record to the full-body mass-poster cutout.
 *
 * By default PNGs are served from the client bundle at `/characters/cutouts/`.
 * For production you can host the same files on object storage (GCS, R2, S3)
 * and set `REACT_APP_CUTOUT_BASE_URL` at build time to the public folder URL,
 * e.g. `https://storage.googleapis.com/your-bucket/cutouts` (no trailing slash).
 *
 * Keyed by a normalized form of the server `name` field so we can tolerate
 * the small spelling / suffix differences in the DB ("Shivaji - The Boss",
 * "Rocky bhai", "Devesena") without modifying the server data.
 */

import { homepageCharacters } from './homepageCharacters';

const cutoutBase = (process.env.REACT_APP_CUTOUT_BASE_URL || '').trim().replace(/\/$/, '');

/**
 * Rewrites `/characters/cutouts/foo.png` to a full URL when
 * `REACT_APP_CUTOUT_BASE_URL` is set. Otherwise returns the path unchanged
 * (served from `public/` in dev and the static host in prod).
 */
export const publicCutoutUrl = (relativePath) => {
    if (!relativePath) return '';
    if (!cutoutBase) return relativePath;
    const p = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    const file = p.startsWith('/characters/cutouts/')
        ? p.slice('/characters/cutouts/'.length)
        : p.replace(/^.*\//, '');
    return `${cutoutBase}/${file}`;
};

/** Normalize for lookup: lowercase, strip non-alphanumeric. */
const normalize = (s) =>
    String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

/**
 * Explicit map of normalized server names → homepage character `id`.
 * Covers the spelling variants ("Shivaji" vs "sivaji", "Devesena" vs
 * "devasena") and the suffixes ("Shivaji - The Boss", "Rocky bhai").
 */
const NAME_TO_ID = {
    shivaji: 'sivaji',
    shivajitheboss: 'sivaji',
    sivaji: 'sivaji',
    sivajitheboss: 'sivaji',
    anniyan: 'anniyan',
    rocky: 'rocky',
    rockybhai: 'rocky',
    kabilan: 'kabilan',
    jagadeesh: 'jagadeesh',
    devasena: 'devasena',
    devesena: 'devasena',
};

const BY_ID = Object.fromEntries(homepageCharacters.map((c) => [c.id, c]));

/**
 * Resolve a server character record (or a plain name string) to a homepage
 * cutout record. Returns null when no match — caller should fall back to
 * the server-supplied `image` URL in that case.
 */
export const resolveCutout = (characterOrName) => {
    const name =
        typeof characterOrName === 'string'
            ? characterOrName
            : characterOrName?.name;
    if (!name) return null;

    const key = normalize(name);
    const id = NAME_TO_ID[key];
    if (!id) return null;

    return BY_ID[id] || null;
};

/** Convenience: get the cutout URL (string) for a character, or null. */
export const cutoutUrlFor = (characterOrName) => {
    const rec = resolveCutout(characterOrName);
    return rec ? publicCutoutUrl(rec.cutoutUrl) : null;
};

/** Convenience: get the billing (e.g. "The Boss") for a character, or null. */
export const billingFor = (characterOrName) => {
    const rec = resolveCutout(characterOrName);
    return rec ? rec.billing : null;
};

export default resolveCutout;
